import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useFormContext } from 'react-hook-form';
import { getError } from '../../../utils/FormError';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faImage, faXmark } from "@fortawesome/free-solid-svg-icons";
import './ImageUploader.scss';

const ImageUploader = ({
    name,
    multiple = false,
    validationRules = {},
    onUpload,
    onDelete,
    images
}) => {
    const [files, setFiles] = useState([]);
    const {
        register,
        setValue,
        getValues,
        trigger,
        formState: { errors }
    } = useFormContext();

    const error = getError(errors, name);

    useEffect(() => {
        register(name, validationRules);
    }, [register, name, validationRules]);

    useEffect(() => {
        const currentValue = getValues(name);

        if (!multiple && currentValue && typeof currentValue === 'string') {
            setFiles([{ preview: currentValue, name: currentValue, isExisting: true }]);
        }

        if (multiple && Array.isArray(currentValue)) {
            setFiles(currentValue.map((val) =>
                typeof val === 'string'
                    ? { preview: val, name: val, isExisting: true }
                    : val
            ));
        }
    }, [getValues, name, multiple]);

    useEffect(() => {
        if (images && images.length > 0) {
            const initialFiles = images.map((img) => ({
                preview: img.url,
                name: img,
                isExisting: true,
                media: img
            }));
            setFiles(initialFiles);
            setValue(name, multiple ? images : images[0], { shouldValidate: true });
            trigger(name);
        }
    }, [images, name, multiple, setValue, trigger]);

    const onDrop = useCallback(
        (acceptedFiles) => {
            const imageFiles = acceptedFiles.map((file) =>
                Object.assign(file, { preview: URL.createObjectURL(file) })
            );

            const newFiles = multiple ? [...files, ...imageFiles] : imageFiles;
            setFiles(newFiles);

            const valueToSet = newFiles.map(f => f.isExisting ? f.name : f);
            setValue(name, multiple ? valueToSet : valueToSet[0], { shouldValidate: true });
            trigger(name);

            if (onUpload) onUpload(newFiles);
        },
        [multiple, files, setValue, trigger, name, onUpload]
    );

    const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
        onDrop,
        accept: { 'image/*': [] },
        multiple,
        noClick: true,
        noKeyboard: true
    });

    const handleRemove = (index) => {
        const fileToRemove = files[index];

        if (fileToRemove.isExisting && onDelete) {
            onDelete([fileToRemove.media]);
        }

        const updated = [...files];
        updated.splice(index, 1);
        setFiles(updated);

        const formValue = updated.map(f => f.isExisting ? f.name : f);
        setValue(name, multiple ? formValue : formValue[0] || null, { shouldValidate: true });
        trigger(name);
    };

    return (
        <div className="image-uploader">
            <div {...getRootProps({ className: 'dropzone' })}>
                <input {...getInputProps()} />
                <div className={`dropzone-area${isDragActive ? ' active' : ''}`}>
                    <div className="dropzone-icon">
                        <FontAwesomeIcon icon={faImage} />
                    </div>
                    <p>{isDragActive ? 'Déposez les images ici...' : 'Glissez-déposez une image, ou'}</p>
                    <button className="dropzone-btn" type="button" onClick={open}>
                        <FontAwesomeIcon icon={faImage} />
                        Choisir une image
                    </button>
                </div>
            </div>

            {files.length > 0 && (
                <div className="previews">
                    {files.map((file, idx) => (
                        <div key={idx} className="preview">
                            <img src={file.preview} alt={`upload-${idx}`} />
                            <button
                                type="button"
                                className="preview-delete"
                                onClick={() => handleRemove(idx)}
                                title="Supprimer"
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {error && (
                <p className="uploader-error">{error.message}</p>
            )}
        </div>
    );
};

export default ImageUploader;